import { createTheme, alpha, Theme } from '@mui/material/styles';
import './fonts.css';
import '../pages/public/publicFonts.css';

// "Ledger" design system (see .interface-design/system.md).
// A calm analyst's desk: ledger-ink navy on paper-white, graphite greys, and ONE accent (the
// Maxulus logo blue) reserved for actions, selection, focus and links. Depth is borders only;
// shadows exist solely on floating layers (menus, popovers, dialogs). Colour that does not mean
// something (status, money in/out, action) is not used.

export type LedgerMode = 'light' | 'dark';

export interface LedgerTokens {
  ink: string;          // primary text, headings, figures
  inkSecondary: string; // supporting text
  inkTertiary: string;  // metadata, table headers, labels
  inkMuted: string;     // disabled, placeholders
  canvas: string;       // page background (and sidebar: same world)
  surface: string;      // panels, tables, cards
  inset: string;        // inputs: slightly sunk below the surface
  hover: string;        // row/item hover wash
  rule: string;         // standard border
  ruleSoft: string;     // separators inside a panel
  ruleStrong: string;   // control outlines, emphasis
  accent: string;       // the single accent
  accentHover: string;
  accentSoft: string;   // selected/active wash
  focus: string;        // focus ring
  gain: string;         // money/metric moving the good way, success
  loss: string;         // the bad way, errors
  alert: string;        // warnings, anomalies
  overlayShadow: string;
}

export const LEDGER: Record<LedgerMode, LedgerTokens> = {
  light: {
    ink: '#07163D',
    inkSecondary: '#3D4A66',
    inkTertiary: '#5F6A82',
    inkMuted: '#9AA3B5',
    canvas: '#F6F7F9',
    surface: '#FFFFFF',
    inset: '#F8F9FB',
    hover: 'rgba(7, 22, 61, 0.035)',
    rule: 'rgba(7, 22, 61, 0.09)',
    ruleSoft: 'rgba(7, 22, 61, 0.06)',
    ruleStrong: 'rgba(7, 22, 61, 0.16)',
    accent: '#0A69F7',
    accentHover: '#0858D1',
    accentSoft: 'rgba(10, 105, 247, 0.08)',
    focus: 'rgba(10, 105, 247, 0.38)',
    // Status colours are also used for small text (deltas), so they meet 4.5:1 on canvas
    gain: '#187A4D',
    loss: '#C8423B',
    alert: '#9A6516',
    overlayShadow: '0 0 0 1px rgba(7, 22, 61, 0.08), 0 8px 24px rgba(7, 22, 61, 0.10), 0 2px 6px rgba(7, 22, 61, 0.05)',
  },
  dark: {
    ink: '#E6EAF2',
    inkSecondary: '#AEB7C8',
    inkTertiary: '#7E899E',
    inkMuted: '#58637A',
    canvas: '#0B1220',
    surface: '#101929',
    inset: '#0D1524',
    hover: 'rgba(255, 255, 255, 0.04)',
    rule: 'rgba(255, 255, 255, 0.08)',
    ruleSoft: 'rgba(255, 255, 255, 0.05)',
    ruleStrong: 'rgba(255, 255, 255, 0.14)',
    accent: '#4D8DFF',
    accentHover: '#6FA3FF',
    accentSoft: 'rgba(77, 141, 255, 0.12)',
    focus: 'rgba(77, 141, 255, 0.45)',
    gain: '#3FB27F',
    loss: '#E5675F',
    alert: '#D9A441',
    overlayShadow: '0 0 0 1px rgba(255, 255, 255, 0.08), 0 12px 32px rgba(0, 0, 0, 0.45)',
  },
};

/** Ledger tokens for the active MUI theme. */
export const ledger = (theme: Theme): LedgerTokens => LEDGER[theme.palette.mode === 'dark' ? 'dark' : 'light'];

export const FONT_UI = '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif';
export const FONT_DISPLAY = '"Outfit", "Inter", system-ui, sans-serif';
export const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

// Radius scale: controls 6, panels 8, dialogs 12. MUI multiplies numeric sx radii by 6.
const R = { control: 6, panel: 8, dialog: 12 };

// Below this viewport width (laptops, and 1920 screens at 125% Windows scaling) the signed-in
// panel turns compact (ModernLayout): type ~6% smaller through the rem base, tighter table rows,
// tabs, menus and chips. Spacing stays 8 and button heights stay, so the 4px grid and the
// 34px icon buttons still line up. Wider screens keep the comfortable sizes.
export const COMPACT_QUERY = '(max-width: 1599.95px)';
export type Density = 'comfortable' | 'compact';

function buildTheme(mode: LedgerMode, density: Density = 'comfortable') {
  const t = LEDGER[mode];
  const c = density === 'compact';
  const transition = (props: string[]) => props.map(p => `${p} 150ms ${EASE_OUT}`).join(', ');

  return createTheme({
    palette: {
      mode,
      primary: { main: t.accent, dark: t.accentHover, light: alpha(t.accent, 0.7), contrastText: '#FFFFFF' },
      // Secondary is neutral ink: the old purple secondary added a second accent everywhere
      secondary: { main: t.inkSecondary, dark: t.ink, light: t.inkTertiary, contrastText: mode === 'dark' ? '#0B1220' : '#FFFFFF' },
      info: { main: t.accent, contrastText: '#FFFFFF' },
      success: { main: t.gain, contrastText: '#FFFFFF' },
      error: { main: t.loss, contrastText: '#FFFFFF' },
      warning: { main: t.alert, contrastText: '#FFFFFF' },
      background: { default: t.canvas, paper: t.surface },
      text: { primary: t.ink, secondary: t.inkSecondary, disabled: t.inkMuted },
      divider: t.rule,
      action: { hover: t.hover, selected: t.accentSoft, focus: t.accentSoft },
    },
    typography: {
      fontFamily: FONT_UI,
      // Scale: ratio ~1.2 on a 13px UI base -> 11 / 13 / 15 / 18 / 22 / 28
      h1: { fontFamily: FONT_DISPLAY, fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.02em' },
      h2: { fontFamily: FONT_DISPLAY, fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.015em' },
      h3: { fontFamily: FONT_DISPLAY, fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.01em' },
      h4: { fontFamily: FONT_DISPLAY, fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.01em' },
      h5: { fontFamily: FONT_DISPLAY, fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.005em' },
      h6: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4 },
      subtitle1: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.45 },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.45 },
      body1: { fontSize: '0.875rem', lineHeight: 1.55 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
      caption: { fontSize: '0.75rem', lineHeight: 1.45 },
      overline: { fontSize: '0.6875rem', fontWeight: 500, letterSpacing: '0.04em', lineHeight: 1.4 },
      button: { textTransform: 'none', fontWeight: 500, fontSize: '0.8125rem' },
    },
    shape: { borderRadius: R.control },
    // Floating layers only. Everything at rest is flat with a border.
    shadows: ['none', ...Array(24).fill(t.overlayShadow)] as any,
    transitions: {
      easing: { easeOut: EASE_OUT, easeInOut: 'cubic-bezier(0.77, 0, 0.175, 1)', easeIn: EASE_OUT, sharp: EASE_OUT },
      duration: { shortest: 120, shorter: 150, short: 180, standard: 200, complex: 240, enteringScreen: 200, leavingScreen: 150 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', backgroundColor: t.canvas },
          '::selection': { backgroundColor: t.accentSoft },
          ':focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': { animationDuration: '1ms !important', transitionDuration: '1ms !important' },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ ownerState }) => ({
            backgroundImage: 'none',
            borderRadius: R.panel,
            // At rest: flat panel with a border. Elevated (menus, popovers, dialogs): overlay shadow.
            ...((ownerState.elevation ?? 0) <= 3
              ? { boxShadow: 'none', border: `1px solid ${t.rule}` }
              : { border: 'none' }),
          }),
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { borderRadius: R.panel, boxShadow: 'none', border: `1px solid ${t.rule}` } },
      },
      MuiButtonBase: { defaultProps: { disableRipple: true } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: R.control,
            padding: '6px 14px',
            minHeight: 34,
            // 1.5 keeps outlined buttons (2px of border) at 34px like contained ones
            lineHeight: 1.5,
            fontSize: '0.8125rem',
            fontWeight: 500,
            boxShadow: 'none',
            transition: `${transition(['background-color', 'color', 'border-color'])}, transform 120ms ${EASE_OUT}`,
            '&:active': { transform: 'scale(0.97)' },
            '&:hover': { boxShadow: 'none' },
          },
          // Heights that hold for outlined buttons too (their 2px border adds to the content)
          sizeSmall: { padding: '4px 10px', minHeight: 30, fontSize: '0.78125rem' },
          sizeLarge: { padding: '9px 18px', minHeight: 42, fontSize: '0.875rem' },
          contained: { '&:hover': { boxShadow: 'none' } },
          containedPrimary: { backgroundColor: t.accent, '&:hover': { backgroundColor: t.accentHover } },
          outlined: {
            borderColor: t.ruleStrong,
            color: t.ink,
            '&:hover': { borderColor: t.ruleStrong, backgroundColor: t.hover },
          },
          outlinedPrimary: { color: t.accent, borderColor: alpha(t.accent, 0.35), '&:hover': { borderColor: t.accent, backgroundColor: t.accentSoft } },
          text: { '&:hover': { backgroundColor: t.hover } },
          textPrimary: { '&:hover': { backgroundColor: t.accentSoft } },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: R.control,
            color: t.inkTertiary,
            transition: `${transition(['background-color', 'color'])}, transform 120ms ${EASE_OUT}`,
            '&:hover': { backgroundColor: t.hover, color: t.ink },
            '&:active': { transform: 'scale(0.95)' },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: R.control,
            backgroundColor: t.inset,
            fontSize: '0.8125rem',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.ruleStrong, transition: transition(['border-color']) },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(t.ink, 0.28) },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.accent, borderWidth: 1, boxShadow: `0 0 0 3px ${t.accentSoft}` },
          },
          input: { '&::placeholder': { color: t.inkMuted, opacity: 1 } },
          // Small single-line fields and selects are 34px like buttons, so header rows line up; the
          // padding follows the line box (1.4375em), so the height holds in both densities
          inputSizeSmall: { '&:not(.MuiInputBase-inputMultiline)': { paddingTop: 'calc((34px - 1.4375em) / 2)', paddingBottom: 'calc((34px - 1.4375em) / 2)' } },
        },
      },
      // Small Autocomplete fields at 34px too (6px root padding on each side + the input's own padding)
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
              paddingTop: 'calc((22px - 1.4375em) / 2)', paddingBottom: 'calc((22px - 1.4375em) / 2)',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          // Centred in the 34px small field (MUI places it for a ~40px one); the floated position is untouched
          root: { fontSize: '0.8125rem', color: t.inkTertiary, '&.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 8px) scale(1)' } },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: R.control, fontWeight: 500, fontSize: '0.75rem', height: c ? 22 : 24 },
          sizeSmall: { height: c ? 20 : 22, fontSize: '0.71875rem' },
          filled: { backgroundColor: alpha(t.ink, mode === 'dark' ? 0.1 : 0.06), color: t.inkSecondary },
          outlined: { borderColor: t.ruleStrong, color: t.inkSecondary },
          // Semantic chips are a soft wash with coloured text, never a solid block
          colorPrimary: { '&.MuiChip-filled': { backgroundColor: t.accentSoft, color: t.accent } },
          colorInfo: { '&.MuiChip-filled': { backgroundColor: t.accentSoft, color: t.accent } },
          colorSuccess: { '&.MuiChip-filled': { backgroundColor: alpha(t.gain, 0.1), color: t.gain } },
          colorError: { '&.MuiChip-filled': { backgroundColor: alpha(t.loss, 0.1), color: t.loss } },
          colorWarning: { '&.MuiChip-filled': { backgroundColor: alpha(t.alert, 0.12), color: t.alert } },
          colorSecondary: { '&.MuiChip-filled': { backgroundColor: alpha(t.ink, 0.06), color: t.inkSecondary } },
        },
      },
      MuiAvatar: { styleOverrides: { root: { backgroundColor: t.accentSoft, color: t.accent, fontWeight: 600, fontSize: '0.8125rem' } } },
      MuiTableContainer: { styleOverrides: { root: { borderRadius: 0 } } },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: t.surface,
              color: t.inkTertiary,
              fontSize: '0.71875rem',
              fontWeight: 500,
              letterSpacing: '0.02em',
              borderBottom: `1px solid ${t.rule}`,
              whiteSpace: 'nowrap',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: c ? '8px 10px' : '10px 12px',
            fontSize: '0.8125rem',
            borderBottom: `1px solid ${t.ruleSoft}`,
            fontVariantNumeric: 'tabular-nums',
            color: t.ink,
          },
          sizeSmall: { padding: c ? '6px 8px' : '7px 10px' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { transition: transition(['background-color']), '&.MuiTableRow-hover:hover': { backgroundColor: t.hover } },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: c ? 36 : 40 },
          indicator: { height: 2, borderRadius: 1, backgroundColor: t.accent },
        },
      },
      MuiTab: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            minHeight: c ? 36 : 40,
            padding: c ? '6px 10px' : '8px 12px',
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: t.inkTertiary,
            transition: transition(['color']),
            '&:hover': { color: t.ink },
            '&.Mui-selected': { color: t.ink },
          },
        },
      },
      MuiTooltip: {
        defaultProps: { enterDelay: 300, enterNextDelay: 0 },
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'dark' ? '#E6EAF2' : '#07163D',
            color: mode === 'dark' ? '#0B1220' : '#FFFFFF',
            fontSize: '0.75rem',
            fontWeight: 500,
            borderRadius: R.control,
            padding: '6px 9px',
          },
          arrow: { color: mode === 'dark' ? '#E6EAF2' : '#07163D' },
        },
      },
      MuiMenu: { styleOverrides: { paper: { borderRadius: R.panel, marginTop: 4 } } },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
            minHeight: c ? 30 : 34,
            borderRadius: 4,
            margin: '0 4px',
            '&:hover': { backgroundColor: t.hover },
            '&.Mui-selected': { backgroundColor: t.accentSoft, '&:hover': { backgroundColor: t.accentSoft } },
          },
        },
      },
      MuiPopover: { styleOverrides: { paper: { borderRadius: R.panel } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: R.dialog } } },
      MuiDialogTitle: { styleOverrides: { root: { fontFamily: FONT_DISPLAY, fontSize: '1.125rem', fontWeight: 600 } } },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: R.panel, fontSize: '0.8125rem', alignItems: 'center' },
          standardInfo: { backgroundColor: t.accentSoft, color: t.ink, border: `1px solid ${alpha(t.accent, 0.18)}`, '& .MuiAlert-icon': { color: t.accent } },
          standardSuccess: { backgroundColor: alpha(t.gain, 0.07), color: t.ink, border: `1px solid ${alpha(t.gain, 0.2)}`, '& .MuiAlert-icon': { color: t.gain } },
          standardWarning: { backgroundColor: alpha(t.alert, 0.08), color: t.ink, border: `1px solid ${alpha(t.alert, 0.22)}`, '& .MuiAlert-icon': { color: t.alert } },
          standardError: { backgroundColor: alpha(t.loss, 0.07), color: t.ink, border: `1px solid ${alpha(t.loss, 0.2)}`, '& .MuiAlert-icon': { color: t.loss } },
        },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { height: 2, backgroundColor: t.ruleSoft }, bar: { backgroundColor: t.accent } },
      },
      MuiSkeleton: { styleOverrides: { root: { backgroundColor: alpha(t.ink, mode === 'dark' ? 0.08 : 0.06) } } },
      MuiDivider: { styleOverrides: { root: { borderColor: t.rule } } },
      MuiAppBar: { defaultProps: { elevation: 0 } },
      MuiDrawer: {
        styleOverrides: { paper: { backgroundColor: t.canvas, color: t.ink, borderRight: `1px solid ${t.rule}`, borderRadius: 0 } },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: { '&.Mui-checked + .MuiSwitch-track': { backgroundColor: t.accent, opacity: 1 } },
          track: { backgroundColor: t.ruleStrong, opacity: 1 },
        },
      },
      MuiCheckbox: { styleOverrides: { root: { color: t.ruleStrong, '&.Mui-checked': { color: t.accent } } } },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: '0.78125rem',
            padding: '4px 10px',
            color: t.inkSecondary,
            borderColor: t.ruleStrong,
            // accentHover: the plain accent on its own soft tint falls short of 4.5:1 for small text
            '&.Mui-selected': { backgroundColor: t.accentSoft, color: t.accentHover, '&:hover': { backgroundColor: t.accentSoft } },
          },
        },
      },
    },
  });
}

const modernTheme = buildTheme('light');
const darkTheme = buildTheme('dark');
const themes = {
  light: { comfortable: modernTheme, compact: buildTheme('light', 'compact') },
  dark: { comfortable: darkTheme, compact: buildTheme('dark', 'compact') },
};
/** The theme for a colour mode and the screen's density */
const themeFor = (mode: LedgerMode, density: Density) => themes[mode][density];

export { modernTheme, darkTheme, themeFor };
